import { Link } from 'react-router';
import { getManagerImage, getManagerEmoji } from '../../utils/managers';
import { castKey } from '../../data/cast';
import CastFigure from '../../motion/CastFigure';
import { managerSlug } from '../../utils/slug';

export function FolioSection({ numeral, align = 'left', className = '', children }) {
  const positionClass = align === 'right'
    ? '-top-14 -right-6 md:-right-8'
    : '-top-14 -left-6 md:-left-8';
  return (
    <section className={`relative overflow-hidden ${className}`}>
      <div
        aria-hidden="true"
        className={`absolute ${positionClass} font-display font-black text-accent-gold/[0.06] leading-none select-none pointer-events-none`}
        style={{ fontSize: 'clamp(180px, 22vw, 300px)' }}
      >
        {numeral}
      </div>
      <div className="relative px-6 md:px-14 py-14 md:py-20">
        {children}
      </div>
    </section>
  );
}

export function SectionMast({ tag, title, dek, note, action, color = 'gold' }) {
  const tagColor = color === 'crimson' ? 'text-accent-red' : 'text-accent-gold';
  const ruleColor = color === 'crimson' ? 'bg-accent-red' : 'bg-accent-gold';
  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-4 md:gap-10 items-end">
      <div className="min-w-0">
        <div className="flex items-center gap-3 mb-4">
          <span className={`block h-px w-10 ${ruleColor}`} />
          <span className={`font-display tracking-[0.35em] text-[11px] font-bold uppercase ${tagColor}`}>
            {tag}
          </span>
        </div>
        <h2 className="font-display font-black uppercase leading-[0.88] tracking-normal text-ink-primary break-words"
            style={{ fontSize: 'clamp(40px, 5.5vw, 80px)' }}>
          {title}
        </h2>
        {dek && (
          <p className="mt-4 font-body italic text-ink-secondary leading-snug max-w-[58ch]"
             style={{ fontSize: 'clamp(15px, 1.15vw, 18px)' }}>
            {dek}
          </p>
        )}
        {note && (
          <div className="mt-4 max-w-[62ch] border-l-2 border-accent-gold/70 pl-4 font-body text-[13px] md:text-sm leading-relaxed text-ink-tertiary">
            {note}
          </div>
        )}
      </div>
      {action && (
        <Link
          to={action.href}
          className="self-end justify-self-start md:justify-self-end font-display tracking-[0.25em] uppercase text-[11px] font-bold text-accent-gold border border-accent-gold px-5 py-3 hover:bg-accent-gold hover:text-bg-deep transition-colors whitespace-nowrap"
        >
          {action.label} &rarr;
        </Link>
      )}
    </div>
  );
}

export function CoverLine({ eyebrow, borderClass, eyebrowClass, title, blurb }) {
  return (
    <div className={`border-t-2 ${borderClass} pt-3 min-w-0`}>
      <div className={`font-display font-bold tracking-[0.3em] text-[9px] uppercase ${eyebrowClass}`}>
        &#x25BE; {eyebrow}
      </div>
      <div className="mt-2 font-display font-black uppercase leading-none tracking-normal text-ink-primary break-words"
           style={{ fontSize: '24px' }}>
        {title}
      </div>
      <p className="mt-2 font-body italic leading-snug text-ink-secondary"
         style={{ fontSize: '12px' }}>
        {blurb}
      </p>
    </div>
  );
}

const SPAN_CLASS = {
  hero: 'col-span-2 md:col-span-3 md:row-span-2',
  medium: 'col-span-2 md:col-span-2',
  small: 'col-span-2 sm:col-span-1 md:col-span-1',
};

const TONE_CLASS = {
  dark: 'bg-bg-surface text-ink-primary',
  cream: 'bg-bg-cream text-ink-cream-strong',
  paper: 'bg-bg-paper text-ink-primary',
  crimson: 'bg-accent-crimson text-ink-primary',
};

export function NumberTile({ tile }) {
  const baseSpan = SPAN_CLASS[tile.span] || SPAN_CLASS.small;
  const baseTone = TONE_CLASS[tile.tone] || TONE_CLASS.dark;
  const isCream = tile.tone === 'cream';
  const markEl = renderTileMark(tile.mark, tile.tone);

  if (tile.kind === 'quote') {
    return (
      <div className={`${baseSpan} ${baseTone} relative min-h-0 h-full p-5 md:p-6 flex flex-col justify-between gap-2 almanac-lift overflow-hidden`}>
        <div className="font-display tracking-[0.22em] text-[10px] uppercase font-bold opacity-80 leading-snug break-words line-clamp-2">
          {tile.label || 'A Verdict'}
        </div>
        <blockquote className="font-display font-black italic leading-[1.05] line-clamp-4 min-h-0"
                    style={{ fontSize: 'clamp(16px, 1.6vw, 24px)' }}>
          {tile.text}
        </blockquote>
        <cite className="not-italic font-body italic text-[11px] opacity-75 leading-snug line-clamp-1">
          - {tile.cite}
        </cite>
      </div>
    );
  }

  if (tile.kind === 'split') {
    return (
      <div className={`${baseSpan} ${baseTone} relative grid grid-cols-2 divide-x divide-white/15 almanac-lift`}>
        <SplitHalf side={tile.left} accent="gold" />
        <SplitHalf side={tile.right} accent="red" />
      </div>
    );
  }

  const valueChars = String(tile.value ?? '').length;
  const valueSize = valueChars > 8
    ? 'clamp(22px, 2.4vw, 32px)'
    : tile.span === 'hero'
      ? 'clamp(52px, 7.2vw, 110px)'
      : tile.span === 'medium'
        ? 'clamp(40px, 4.5vw, 64px)'
        : 'clamp(28px, 2.8vw, 40px)';
  const accentText = isCream ? 'text-accent-rust' : 'text-accent-gold';
  const subText = isCream ? 'text-ink-cream-strong/70' : 'text-ink-tertiary';

  return (
    <div className={`${baseSpan} ${baseTone} relative min-h-0 h-full p-4 md:p-5 flex flex-col justify-between gap-2 almanac-lift overflow-hidden`}>
      {markEl}
      <div className={`font-display tracking-[0.18em] text-[10px] uppercase font-bold leading-snug break-words pr-4 line-clamp-2 ${accentText}`}>
        {tile.unit}
      </div>
      <div className={`font-mono font-bold leading-[0.9] min-w-0 max-w-full overflow-hidden text-ellipsis ${isCream ? 'text-ink-cream-strong' : 'text-ink-primary'}`}
           style={{ fontSize: valueSize, letterSpacing: 0 }}>
        {tile.value}
      </div>
      <div className="min-h-0 space-y-1">
        <div className={`font-body italic leading-snug line-clamp-3 ${isCream ? 'text-ink-cream-strong' : 'text-ink-primary'}`}
             style={{ fontSize: tile.span === 'hero' ? '16px' : '13px', fontWeight: 500 }}>
          {tile.caption}
        </div>
        {tile.sub && (
          <div className={`font-body italic text-[11px] leading-snug line-clamp-2 ${subText}`}>
            {tile.sub}
          </div>
        )}
      </div>
    </div>
  );
}

function SplitHalf({ side, accent }) {
  const accentClass = accent === 'gold' ? 'text-accent-gold' : 'text-accent-red';
  return (
    <div className="p-4 md:p-5 flex flex-col justify-between min-w-0">
      <div className={`font-display tracking-[0.18em] text-[10px] uppercase font-bold leading-snug break-words ${accentClass}`}>
        {side.label}
      </div>
      <div className="font-mono font-bold leading-[0.9] text-ink-primary"
           style={{ fontSize: 'clamp(28px, 3.2vw, 44px)', letterSpacing: 0 }}>
        {side.value}
      </div>
      <div className="font-body italic text-[11px] text-ink-tertiary leading-snug break-words">
        {side.sub}
      </div>
    </div>
  );
}

function renderTileMark(mark, tone) {
  const isCream = tone === 'cream';
  const goldClass = isCream ? 'text-accent-rust' : 'text-accent-gold';
  const ruleClass = isCream ? 'bg-accent-rust' : 'bg-accent-gold';
  switch (mark) {
    case 'tick':
      return <div className={`absolute top-3 right-3 font-display font-bold text-[14px] ${goldClass}`}>&#x25BE;</div>;
    case 'top-rule':
      return <div className={`absolute top-0 left-0 h-[3px] w-12 ${ruleClass}`} />;
    case 'left-rule':
      return <div className={`absolute top-0 left-0 w-[3px] h-12 ${ruleClass}`} />;
    case 'none':
    default:
      return null;
  }
}

export function YearbookRow({ entry, index }) {
  const image = getManagerImage(entry.name);

  const key = castKey(entry.name);
  const photo = key ? (
    <div className="flex-shrink-0 w-[76px] md:w-[96px] aspect-square paper border-t-2 border-accent-gold flex items-end justify-center overflow-hidden">
      <CastFigure name={entry.name} alt={entry.name} trigger="hover" sequence={['shock', 'point', 'smug']} className="w-[88%] h-[94%] translate-y-[3%]" />
    </div>
  ) : image ? (
    <div className="flex-shrink-0 w-[76px] md:w-[96px]">
      <img
        src={`/images/managers/${encodeURIComponent(image)}`}
        alt={entry.name}
        className="w-full aspect-square object-cover border-t-2 border-accent-gold"
        style={{ filter: 'grayscale(0.35) contrast(1.08)' }}
      />
    </div>
  ) : (
    <div className="flex-shrink-0 w-[76px] md:w-[96px] aspect-square bg-bg-elevated flex items-center justify-center text-4xl">
      {getManagerEmoji(entry.name, '\u{1F3C8}')}
    </div>
  );

  return (
    <Link
      to={`/managers/${managerSlug(entry.name)}`}
      className="group flex items-start gap-4 md:gap-6 px-2 md:px-5 py-5 md:py-6 hover:bg-bg-paper/50 transition-colors"
    >
      {photo}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-accent-gold tracking-[0.18em] font-bold uppercase">
          <span>NO. {String(index + 1).padStart(2, '0')}</span>
          <span className="text-accent-gold/40">/</span>
          <span>{entry.role}</span>
        </div>
        <div className="mt-1 font-display font-black uppercase leading-[0.9] tracking-normal text-ink-primary group-hover:text-accent-gold transition-colors break-words"
             style={{ fontSize: 'clamp(30px, 4vw, 54px)' }}>
          {entry.name}
        </div>
        <p className="mt-2 font-body italic text-ink-secondary leading-snug max-w-[68ch]"
           style={{ fontSize: 'clamp(14px, 1.1vw, 17px)' }}>
          {entry.receipt}
        </p>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-baseline gap-1.5 sm:gap-3">
          <span className="font-display tracking-[0.18em] text-[10px] uppercase font-bold text-accent-red">
            2026 Assignment
          </span>
          <span className="font-body italic text-sm text-ink-tertiary leading-snug">
            {entry.assignment}
          </span>
        </div>
      </div>
    </Link>
  );
}

const HIGHLIGHT_SPAN = {
  wide: 'col-span-2 md:col-span-3',
  tall: 'col-span-2 md:col-span-2 md:row-span-2',
  small: 'col-span-2 md:col-span-2',
};

const HIGHLIGHT_TONE = {
  gold: 'bg-accent-gold text-bg-deep',
  dark: 'bg-bg-surface text-ink-primary',
  cream: 'bg-bg-cream text-ink-cream-strong',
  crimson: 'bg-accent-crimson text-ink-primary',
};

export function HighlightTile({ tile }) {
  const span = HIGHLIGHT_SPAN[tile.span] || HIGHLIGHT_SPAN.small;
  const tone = HIGHLIGHT_TONE[tile.tone] || HIGHLIGHT_TONE.dark;
  const isGold = tile.tone === 'gold';
  const isCream = tile.tone === 'cream';
  const eyebrowClass = isGold || isCream ? 'text-bg-deep/70' : 'text-accent-gold';

  return (
    <div className={`${span} ${tone} relative min-h-[182px] md:min-h-0 p-5 md:p-6 flex flex-col justify-between almanac-lift overflow-hidden`}>
      {isGold && (
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at center, rgba(10,14,26,0.55) 1px, transparent 1.5px)',
            backgroundSize: '8px 8px',
          }}
        />
      )}
      <div className="relative">
        <div className={`font-display tracking-[0.3em] text-[10px] uppercase font-bold ${eyebrowClass}`}>
          {tile.eyebrow}
        </div>
      </div>
      <div className="relative">
        <div className="font-display font-black uppercase leading-[0.92] tracking-normal break-words"
             style={{ fontSize: tile.span === 'tall' ? 'clamp(28px, 2.8vw, 44px)' : 'clamp(22px, 2vw, 32px)' }}>
          {tile.headline}
        </div>
        <div className="mt-2 font-body italic leading-snug max-w-[42ch] opacity-80"
             style={{ fontSize: '13px', fontWeight: 500 }}>
          {tile.body}
        </div>
      </div>
    </div>
  );
}

export function ShameCard({ card }) {
  const image = getManagerImage(card.winner);
  return (
    <article className="relative bg-bg-cream text-ink-cream-strong almanac-lift">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(251,191,36,0.4) 0%, rgba(249,115,22,0.2) 50%, rgba(251,191,36,0.4) 100%)',
          padding: '2px',
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />
      <header className="relative px-4 py-3 border-b-2 border-accent-rust/40 flex items-center justify-between">
        <div className="font-display tracking-[0.3em] text-[10px] uppercase font-bold text-accent-rust">
          The Almanac
        </div>
        <div className="font-mono text-[10px] font-bold text-ink-cream-strong/70">
          #{card.cardNo}
        </div>
      </header>
      <div className="relative bg-gradient-to-b from-amber-100 to-amber-200 aspect-[4/5] overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30 pointer-events-none mix-blend-multiply"
          style={{
            backgroundImage: 'radial-gradient(circle at center, rgba(61,40,23,0.45) 1px, transparent 1.5px)',
            backgroundSize: '6px 6px',
          }}
        />
        {castKey(card.winner) ? (
          <div className="absolute inset-x-[6%] top-[6%] bottom-[14%] flex items-end justify-center">
            <CastFigure name={card.winner} alt={card.winner} trigger="inview" sequence={['facepalm', 'shock']} hold={2400} className="w-full h-full" />
          </div>
        ) : image ? (
          <img
            src={`/images/managers/${encodeURIComponent(image)}`}
            alt={card.winner}
            className="w-full h-full object-cover"
            style={{ filter: 'grayscale(0.45) contrast(1.15) sepia(0.15)' }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-7xl">
            {getManagerEmoji(card.winner, '\u{1F4A2}')}
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-accent-crimson text-ink-primary px-4 py-2">
          <div className="font-display font-black uppercase tracking-normal leading-none"
               style={{ fontSize: 'clamp(22px, 2vw, 30px)' }}>
            {card.winner}
          </div>
        </div>
      </div>
      <div className="relative px-4 py-4 space-y-3">
        <div className="font-display font-black uppercase leading-[0.95] text-ink-cream-strong"
             style={{ fontSize: 'clamp(20px, 1.8vw, 28px)' }}>
          {card.award}
        </div>
        <p className="font-body italic leading-snug text-ink-cream-strong/80 text-sm">
          {card.story}
        </p>
      </div>
      <footer className="relative px-4 py-3 border-t border-accent-rust/30 flex items-center justify-between font-display tracking-[0.25em] text-[9px] uppercase font-bold text-ink-cream-strong/60">
        <span>{card.series}</span>
        <span className="font-mono text-[9px]">CARD {card.cardNo}</span>
      </footer>
    </article>
  );
}

export function CountdownUnit({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-mono font-bold text-ink-primary leading-none"
           style={{ fontSize: 'clamp(56px, 9vw, 110px)', letterSpacing: 0 }}>
        {value}
      </div>
      <div className="mt-2 font-display tracking-[0.3em] text-[10px] md:text-xs font-bold text-accent-gold uppercase">
        {label}
      </div>
    </div>
  );
}

export function Colon() {
  return (
    <div className="font-mono font-bold text-accent-gold/40 leading-none"
         style={{ fontSize: 'clamp(40px, 7vw, 90px)', letterSpacing: 0 }}>
      :
    </div>
  );
}
